// Story 16.5 — una organización: nombre, miembros e invitaciones. Quien tiene
// `manage_members` (su owner o un admin) cambia roles, quita miembros e invita
// con un link de un solo uso que copia y manda por donde quiera.

import { useParams } from '@tanstack/react-router';
import { Check, Copy, Link2, Trash2, UserPlus } from 'lucide-react';
import { type FormEvent, useState } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useMe } from '@/hooks/queries/use-me';
import {
  type OrganizationSummary,
  useOrganization,
  useOrganizationInvitations,
  useOrganizationMembers,
  useOrganizationMutations,
} from '@/hooks/queries/use-organizations';
import { ORGANIZATION_ROLES, ROLE_DESCRIPTIONS, ROLE_LABELS } from '@/lib/organization-roles';
import type { OrganizationRole } from '@/lib/permissions';

const selectClass =
  'block h-9 rounded-md border border-input bg-background px-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring';

function errorMessage(err: unknown, fallback: string): string {
  return err instanceof Error && err.message ? err.message : fallback;
}

function RoleSelect(props: {
  id: string;
  value: OrganizationRole;
  onChange: (role: OrganizationRole) => void;
  disabled?: boolean;
  label: string;
}): React.ReactElement {
  return (
    <select
      id={props.id}
      aria-label={props.label}
      className={selectClass}
      value={props.value}
      disabled={props.disabled}
      onChange={(e) => props.onChange(e.target.value as OrganizationRole)}
    >
      {ORGANIZATION_ROLES.map((role) => (
        <option key={role} value={role} title={ROLE_DESCRIPTIONS[role]}>
          {ROLE_LABELS[role]}
        </option>
      ))}
    </select>
  );
}

function RenameForm({ organization }: { organization: OrganizationSummary }): React.ReactElement {
  const [name, setName] = useState(organization.name);
  const { rename } = useOrganizationMutations(organization.id);
  const changed = name.trim() !== organization.name && name.trim().length > 0;

  async function handleSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    try {
      await rename.mutateAsync(name.trim());
      toast.success('Nombre actualizado');
    } catch (err) {
      toast.error(errorMessage(err, 'No se pudo cambiar el nombre'));
    }
  }

  return (
    <form onSubmit={handleSubmit} className="flex items-end gap-2">
      <div className="flex-1 space-y-1">
        <Label htmlFor="org-rename">Nombre</Label>
        <Input
          id="org-rename"
          value={name}
          onChange={(e) => setName(e.target.value)}
          maxLength={100}
        />
      </div>
      <Button type="submit" variant="outline" disabled={!changed || rename.isPending}>
        Guardar
      </Button>
    </form>
  );
}

function MembersCard({ organizationId }: { organizationId: number }): React.ReactElement {
  const { data: me } = useMe();
  const members = useOrganizationMembers(organizationId, true);
  const { changeRole, removeMember } = useOrganizationMutations(organizationId);

  async function handleRole(userId: number, role: OrganizationRole): Promise<void> {
    try {
      await changeRole.mutateAsync({ userId, role });
      toast.success('Rol actualizado');
    } catch (err) {
      toast.error(errorMessage(err, 'No se pudo cambiar el rol'));
    }
  }

  async function handleRemove(userId: number, email: string): Promise<void> {
    if (!window.confirm(`¿Quitar a ${email} de la organización?`)) return;
    try {
      await removeMember.mutateAsync(userId);
      toast.success(`${email} ya no es miembro`);
    } catch (err) {
      toast.error(errorMessage(err, 'No se pudo quitar al miembro'));
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Miembros</CardTitle>
      </CardHeader>
      <CardContent>
        {members.isLoading ? (
          <p className="text-sm text-muted-foreground">Cargando miembros…</p>
        ) : !members.data?.length ? (
          <p className="text-sm text-muted-foreground">
            Todavía no hay miembros: invita al owner del cliente.
          </p>
        ) : (
          <ul className="divide-y divide-border">
            {members.data.map((m) => (
              <li key={m.userId} className="flex flex-wrap items-center gap-x-3 gap-y-1 py-2">
                <span className="min-w-0 flex-1 basis-48 text-sm wrap-break-word">
                  <span className="font-medium">{m.name ?? m.email}</span>
                  {m.name && <span className="ml-2 text-muted-foreground">{m.email}</span>}
                  {m.userId === me?.id && <span className="ml-2 text-muted-foreground">(tú)</span>}
                </span>
                <RoleSelect
                  id={`member-role-${m.userId}`}
                  label={`Rol de ${m.email}`}
                  value={m.role}
                  disabled={changeRole.isPending}
                  onChange={(role) => void handleRole(m.userId, role)}
                />
                <Button
                  size="icon"
                  variant="ghost"
                  aria-label={`Quitar a ${m.email}`}
                  disabled={removeMember.isPending}
                  onClick={() => void handleRemove(m.userId, m.email)}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </li>
            ))}
          </ul>
        )}
        <p className="mt-3 text-xs text-muted-foreground">
          La organización siempre necesita al menos un owner.
        </p>
      </CardContent>
    </Card>
  );
}

function InvitationLink({ path }: { path: string }): React.ReactElement {
  const url = `${window.location.origin}${path}`;
  const [copied, setCopied] = useState(false);

  async function copy(): Promise<void> {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
    } catch {
      toast.error('No se pudo copiar: selecciona el link y cópialo a mano');
    }
  }

  return (
    <div className="space-y-2 rounded-md border border-success/30 bg-success/5 p-3">
      <p className="text-sm">
        Copia este link y mándalo por donde quieras. Sirve una sola vez, vence en 7 días y no se
        vuelve a mostrar.
      </p>
      <div className="flex gap-2">
        <Input readOnly value={url} aria-label="Link de invitación" className="font-mono text-xs" />
        <Button type="button" variant="outline" onClick={() => void copy()}>
          {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
          {copied ? 'Copiado' : 'Copiar'}
        </Button>
      </div>
    </div>
  );
}

function InvitationsCard({ organizationId }: { organizationId: number }): React.ReactElement {
  const invitations = useOrganizationInvitations(organizationId, true);
  const { invite, revokeInvitation } = useOrganizationMutations(organizationId);
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<OrganizationRole>('editor');
  const [lastPath, setLastPath] = useState<string | null>(null);

  async function handleSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    try {
      const created = await invite.mutateAsync({ email: email.trim(), role });
      setLastPath(created.path);
      setEmail('');
    } catch (err) {
      toast.error(errorMessage(err, 'No se pudo crear la invitación'));
    }
  }

  async function handleRevoke(id: number, to: string): Promise<void> {
    try {
      await revokeInvitation.mutateAsync(id);
      toast.success(`Invitación a ${to} revocada`);
    } catch (err) {
      toast.error(errorMessage(err, 'No se pudo revocar la invitación'));
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Invitaciones</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <form onSubmit={handleSubmit} className="flex flex-col gap-2 sm:flex-row sm:items-end">
          <div className="flex-1 space-y-1">
            <Label htmlFor="invite-email">Email</Label>
            <Input
              id="invite-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="ana@pizzeria.mx"
              required
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="invite-role">Rol</Label>
            <RoleSelect id="invite-role" label="Rol" value={role} onChange={setRole} />
          </div>
          <Button type="submit" disabled={invite.isPending || !email.trim()}>
            <UserPlus className="h-4 w-4" />
            Invitar
          </Button>
        </form>

        {lastPath && <InvitationLink path={lastPath} />}

        {invitations.data && invitations.data.length > 0 && (
          <ul className="divide-y divide-border">
            {invitations.data.map((inv) => (
              <li key={inv.id} className="flex flex-wrap items-center gap-x-3 gap-y-1 py-2 text-sm">
                <Link2 className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
                <span className="min-w-0 flex-1 basis-48 wrap-break-word">
                  {inv.email} · {ROLE_LABELS[inv.role]}
                </span>
                <span className={inv.expired ? 'text-destructive' : 'text-muted-foreground'}>
                  {inv.expired
                    ? 'Vencida'
                    : `Vence el ${new Date(inv.expiresAt).toLocaleDateString()}`}
                </span>
                <Button
                  size="sm"
                  variant="ghost"
                  disabled={revokeInvitation.isPending}
                  onClick={() => void handleRevoke(inv.id, inv.email)}
                >
                  Revocar
                </Button>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

export function OrganizationPage(): React.ReactElement {
  const params = useParams({ strict: false }) as { organizationId?: string };
  const organizationId = Number(params.organizationId);
  const { data: organization, isLoading, isError } = useOrganization(organizationId);

  if (isLoading) return <p className="text-sm text-muted-foreground">Cargando organización…</p>;
  if (isError || !organization) {
    return <p className="text-sm text-destructive">No se encontró la organización.</p>;
  }
  const canManage = organization.capabilities.includes('manage_members');

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-2xl font-semibold tracking-tight">{organization.name}</h1>
        <p className="text-sm text-muted-foreground">
          <span className="font-mono">{organization.slug}</span> · {organization.botCount}{' '}
          {organization.botCount === 1 ? 'bot' : 'bots'}
          {organization.role && <> · Tu rol: {ROLE_LABELS[organization.role]}</>}
        </p>
      </header>

      {canManage ? (
        <>
          <Card>
            <CardContent className="pt-6">
              <RenameForm key={organization.name} organization={organization} />
            </CardContent>
          </Card>
          <MembersCard organizationId={organization.id} />
          <InvitationsCard organizationId={organization.id} />
        </>
      ) : (
        <p className="text-sm text-muted-foreground">
          Los miembros y las invitaciones los gestiona el owner de la organización.
        </p>
      )}
    </div>
  );
}
